import { ConsoleOutputLevel, OutputToConsoleFunc } from "../../@types"
import { defaultSessionProcessFunc } from "./defaultSessionProcessFunc"

/**
 * An interface representing an object with an execute method, generally a
 * {@link Test}.
 */
export interface Executable {
  /**
   * Execute a test and return a Promise that resolves to a provider-defined
   * test result object.
   */
  execute(): Promise<unknown>
}

/**
 * Function that processes a session configuration and produces executable tests.
 *
 * This function handles the complex initialization logic required to convert
 * configuration data into ready-to-execute measurement tests. The async nature
 * supports sophisticated initialization patterns like:
 *
 * - Sentinel detection for tracker blocking
 * - Network connectivity validation
 * - Resource pre-loading and validation
 * - Complex dependency resolution
 *
 * @param config The session configuration to process
 * @returns Promise resolving to array of executable tests
 */
export type ProduceExecutablesFunc = (
  sessionConfiguration: unknown,
) => Promise<Executable[]>

export type FetchSessionConfigurationFunc = () => Promise<unknown>

export type SessionStartupFuncs = {
  fetchSessionConfiguration: FetchSessionConfigurationFunc
  produceExecutables: ProduceExecutablesFunc
}

/**
 * Encapsulates the result of one provider's RUM session.
 */
export interface SessionResult {
  /**
   * An array containing the test result bundles for each individual test
   * performed.
   */
  testResults: unknown[]
}

/**
 * Called internally if a non-zero `sessionConfigDelayInMilliseconds` is passed
 * by the client setting has been specified.
 */
export function startLater(
  sessionConfigDelayInMilliseconds: number,
  sessionStartupFuncs: SessionStartupFuncs[],
  outputToConsole: OutputToConsoleFunc,
): Promise<SessionResult> {
  return new Promise((resolve) => {
    setTimeout(() => {
      start(sessionStartupFuncs, outputToConsole).then((result) =>
        resolve(result),
      )
    }, sessionConfigDelayInMilliseconds)
  })
}

/**
 * Orchestrates RUM measurement session initialization and execution.
 *
 * Processes multiple configuration sources concurrently with graceful error handling.
 * Failed sources don't prevent successful ones from executing.
 *
 * @param sessionStartupFuncs Array of configuration source functions
 * @param outputToConsole Logging function
 * @returns Promise resolving to combined test results
 */
export async function start(
  sessionStartupFuncs: SessionStartupFuncs[],
  outputToConsole: OutputToConsoleFunc,
): Promise<SessionResult> {
  // Handle config fetching errors gracefully
  const configResults = await Promise.allSettled(
    sessionStartupFuncs.map((funcs) => funcs.fetchSessionConfiguration()),
  )

  // Handle produceExecutables errors gracefully too
  const executablePromises = configResults.map(async (result, idx) => {
    if (result.status === "fulfilled" && "value" in result) {
      try {
        return await sessionStartupFuncs[idx].produceExecutables(result.value)
      } catch (error) {
        // Log error but return empty array to continue processing
        outputToConsole(
          ConsoleOutputLevel.warn,
          () =>
            "Failed to produce executables: " +
            `${error instanceof Error ? error.message : String(error)}`,
        )
        return []
      }
    }
    return []
  })

  const executableResults = await Promise.allSettled(executablePromises)

  const executables: Executable[] = []
  executableResults.forEach((result) => {
    if (result.status === "fulfilled") {
      executables.push(...result.value)
    }
  })

  const process = defaultSessionProcessFunc
  return process(executables)
}
